/* istanbul ignore file */
import axios from 'axios';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import memoize from 'lodash/memoize';
import { CHCKISSUEMNG_TYPES } from '../../constants';
import {
  memoizeWithToken,
  setTokenAsCacheKey,
  setTokenAndParamsAsCacheKey
} from '../utils/memoizeUtils';

export function createIssuesVoids(payload) {
  return from(axios.post('/positive-pay/issue/void/create', payload)).pipe(
    map(({ data }) => data)
  );
}

export function deleteIssueFile(ppImportStatusId) {
  return from(axios.post('/positive-pay/positive-pay/deleteFile', ppImportStatusId)).pipe(
    map(({ data }) => data)
  );
}

export function getAccounts(payload) {
  return from(axios.post('/positive-pay/positive-pay/getAccounts', payload)).pipe(
    map(({ data }) => ({
      ...data,
      data: data.success && !Array.isArray(data.data) ? [] : data.data
    }))
  );
}

export function getCDAccounts(payload) {
  return from(axios.post('/positive-pay/controlled-disbursements/getAccounts', payload)).pipe(
    map(({ data }) => ({
      ...data,
      data: data.success && !Array.isArray(data.data) ? [] : data.data
    }))
  );
}

export function getCheckImage(payload) {
  return from(axios.post('/positive-pay/positive-pay/getCheckImage', payload)).pipe(
    map(({ data }) => data)
  );
}

export function getExceptionDetail(exceptionId) {
  return from(axios.post('/positive-pay/positive-pay/getExceptionDetail', exceptionId)).pipe(
    map(({ data }) => data)
  );
}

export function getPositivePayPreferences() {
  return from(axios.post('/positive-pay/positive-pay/getPositivePayPreferences')).pipe(
    map(({ data }) => data)
  );
}

export function applyDecisionAction(payload) {
  return from(axios.post('/positive-pay/positive-pay/applyDecisionAction', payload)).pipe(
    map(({ data }) => data)
  );
}

export const getConfig = memoize(
  () =>
    from(axios.post('/positive-pay/positive-pay/getConfig')).pipe(
      map(({ data: { data } }) => data)
    ),
  setTokenAsCacheKey
);

export function getExceptions(payload) {
  return from(axios.post('/positive-pay/positive-pay/getExceptions', payload)).pipe(
    map(({ data }) => data)
  );
}

export function getSettings() {
  return from(axios.post('/positive-pay/settings')).pipe(map(({ data }) => data));
}

export function getPPReturnReasons(payload) {
  return from(axios.post('/positive-pay/positive-pay/getPPReturnReasons', payload)).pipe(
    map(({ data }) => data)
  );
}

export const getAccountsList = memoizeWithToken(() =>
  from(axios.post('/positive-pay/positive-pay/getAccounts')).pipe(
    map(({ data: { data } }) => data)
  )
);

export function getFailedImportReview() {
  return from(axios.post('/positive-pay/positive-pay/getFailedImportReview')).pipe(
    map(({ data }) => data)
  );
}

export const getSystemOption = memoize(
  (payload) => {
    return from(axios.post('/infrastructure/config/getSystemOption', payload)).pipe(
      map(({ data: { data, success } }) => ({ data, success }))
    );
  },
  (payload) => setTokenAndParamsAsCacheKey(payload)
);

export function getFailedImportDetails(id) {
  return from(axios.post('/positive-pay/positive-pay/getFailedImportDetails', id)).pipe(
    map(({ data }) => data)
  );
}

export function getIssues(params) {
  return from(axios.post('/positive-pay/positive-pay/getIssues', params)).pipe(
    map(({ data }) => data)
  );
}

export function getImportDetailsHeader(id) {
  return from(axios.post('/positive-pay/positive-pay/getImportDetailsHeader', id)).pipe(
    map(({ data }) => data)
  );
}

export function getImportDetails(params) {
  return from(axios.post('/positive-pay/positive-pay/getImportDetails', params)).pipe(
    map(({ data }) => data)
  );
}

export function getImportIssueFilesList() {
  return from(axios.post('/positive-pay/positive-pay/getImportReview')).pipe(
    map(({ data }) => data)
  );
}

export function getImportMapping() {
  return from(axios.post('/positive-pay/positive-pay/getImportMapping')).pipe(
    map(({ data }) => data)
  );
}

export const getCheckIssuesPermissions = memoizeWithToken(
  (
    neededPermission = [
      CHCKISSUEMNG_TYPES.PPImport,
      CHCKISSUEMNG_TYPES.PPIssuReport,
      CHCKISSUEMNG_TYPES.PPManualIssu,
      CHCKISSUEMNG_TYPES.PPManualVoid
    ]
  ) =>
    from(axios.post('/positive-pay/positive-pay/getPermissions', neededPermission)).pipe(
      map(({ data: { data } }) => data),
      map((permissions) =>
        permissions.reduce(
          (acc, type) => ({
            ...acc,
            [type]: true
          }),
          {}
        )
      )
    )
);

export function updateImportMapping(payload) {
  return from(axios.post('/positive-pay/positive-pay/updateImportMapping', payload)).pipe(
    map(({ data }) => data)
  );
}

export function uploadIssuesVoidsFile(file, format = 'csv') {
  const fileFormat = format === 'csv' ? 'csv' : 'fixed';
  return from(axios.post(`/positive-pay/issue/void/import/${fileFormat}`, file)).pipe(
    map(({ data }) => data)
  );
}

export function deleteIssueVoid(data) {
  return from(axios.post('/positive-pay/positive-pay/deleteIssuesVoids', data)).pipe(
    map(({ data }) => data)
  );
}

export function checkValidation(data) {
  return from(axios.post('/positive-pay/positive-pay/checkValidation', data)).pipe(
    map(({ data }) => data)
  );
}

export function checkFullService() {
  return from(axios.post('/positive-pay/positive-pay/checkFullService')).pipe(
    map(({ data }) => data)
  );
}

export function getPayeeCount() {
  return from(axios.post('/positive-pay/positive-pay/payee/getNumber')).pipe(
    map(({ data }) => data)
  );
}

export function getControlledDisbursementsReport(payload) {
  return from(axios.post('/positive-pay/controlled-disbursements/getCDReport', payload)).pipe(
    map(({ data }) => data)
  );
}

export function getControlledDisbursementsReportDetails(payload) {
  return from(axios.post('/positive-pay/controlled-disbursements/getDetailView', payload)).pipe(
    map(({ data }) => data)
  );
}

export const getCDReportsDates = memoize(
  () =>
    from(axios.post('positive-pay/controlled-disbursements/getCalendar')).pipe(
      map(({ data: { data } }) => data)
    ),
  setTokenAsCacheKey
);
